import { jsx as _jsx, jsxs as _jsxs } from "react/jsx-runtime";
/**
 * CredentialsTable — the §8.10 (R-17) DYNAMIC per-org credentials editor.
 * A controlled CRUD table over `runtimeConfig.llm.credentials`: one row per
 * credential (slug id · provider · kind · masked secret · custom-kind extras ·
 * delete). Add/remove providers dynamically — no more static fields.
 *
 * Semantics (mirror the lexserver C1 contract):
 *  - GET delivers each entry with `secret: "***MASKED***"`; saving the sentinel
 *    preserves the stored ciphertext (re-save without retyping).
 *  - A PATCH replaces the WHOLE Record (entry deletion = omit the id).
 *  - `oauth` kind is NOT creatable here (the OAuth Connections panel owns it).
 *
 * Pure/controlled — the parent owns state + persistence (same pattern as
 * `AliasMatrix`). Ids are commit-on-blur (stable React keys while typing).
 */
import { Eye, EyeOff, Plus, Trash2 } from "lucide-react";
import { useEffect, useState } from "react";
import { useOrgAdminT } from "../i18n/context.js";
const MASKED = "***MASKED***";
// Inlined provider list (browser-safe — see the AliasMatrix note). `oauth`
// providers are connected via the OAuth panel, so openai-codex is not offered.
const PROVIDER_OPTIONS = ["anthropic", "openai", "google", "xai", "openrouter", "custom"];
/** Providers whose credential is a flat api key vs a custom endpoint entry. */
const CUSTOM_KIND_PROVIDERS = new Set(["openrouter", "custom"]);
const ID_RE = /^[a-z][a-z0-9-]*$/;
/** Server caps credential ids at 48 chars ([org-runtime-config.ts] slug regex
 *  `.max(48)`). Mirror it client-side so an over-long id fails the pre-save
 *  guard here instead of 400-ing the whole atomic PATCH (which would roll back
 *  any alias edits bundled in the same save). */
const MAX_ID_LEN = 48;
const INPUT_CLASS = "rounded-md border border-slate-300 bg-white px-2 py-1 text-sm dark:border-slate-600 dark:bg-slate-800";
/** Pre-submit check — mirrors the server's write-gate so a bad row fails HERE
 *  instead of 400-ing the whole atomic PATCH (which would roll back any alias
 *  edits bundled in the same save). Masked secrets count as present. */
export function findIncompleteCredential(table, t) {
    for (const [id, e] of Object.entries(table)) {
        if (!e.secret?.trim())
            return t("credentials.validation.missingSecret", { id });
        if (e.secret.length > 8192)
            return t("credentials.validation.secretTooLong", { id });
        if (!ID_RE.test(id))
            return t("credentials.validation.invalidId", { id });
        if (id.length > MAX_ID_LEN)
            return t("credentials.validation.idTooLong", { id, max: MAX_ID_LEN });
        // kind/provider coherence (the server rejects incoherent pairs — they
        // would be silently inert at merge time).
        const wantCustom = CUSTOM_KIND_PROVIDERS.has(e.provider);
        if (wantCustom && e.kind !== "custom") {
            return t("credentials.validation.kindCustom", { id, provider: e.provider });
        }
        if (!wantCustom && e.kind !== "api-key") {
            return t("credentials.validation.kindApiKey", { id, provider: e.provider });
        }
        if (e.kind === "custom") {
            if (e.provider === "custom" && !e.baseUrl?.trim()) {
                return t("credentials.validation.baseUrlRequired", { id });
            }
            if (e.baseUrl?.trim()) {
                if (e.baseUrl.length > 400)
                    return t("credentials.validation.baseUrlTooLong", { id });
                try {
                    new URL(e.baseUrl);
                }
                catch {
                    return t("credentials.validation.baseUrlInvalid", { id });
                }
            }
            const models = e.models ?? [];
            if (models.length > 16)
                return t("credentials.validation.tooManyModels", { id });
            const longModel = models.find((m) => m.length > 200);
            if (longModel)
                return t("credentials.validation.modelIdTooLong", { id, model: longModel.slice(0, 40) });
        }
    }
    return null;
}
export function CredentialsTable({ value, onChange, disabled }) {
    const t = useOrgAdminT();
    const ids = Object.keys(value);
    const setEntry = (id, entry) => {
        const next = { ...value };
        if (entry === undefined)
            delete next[id];
        else
            next[id] = entry;
        onChange(next);
    };
    const renameEntry = (from, to) => {
        const next = {};
        for (const [k, v] of Object.entries(value))
            next[k === from ? to : k] = v;
        onChange(next);
    };
    const addEntry = () => {
        let n = 1;
        let id = "new-credential";
        while (value[id] !== undefined)
            id = `new-credential-${++n}`;
        setEntry(id, { provider: "openrouter", kind: "custom", secret: "" });
    };
    return (_jsxs("div", { className: "space-y-2", children: [_jsx("div", { className: "overflow-x-auto", children: _jsxs("table", { className: "w-full min-w-[720px] border-collapse text-sm", children: [_jsx("thead", { children: _jsxs("tr", { className: "text-left text-xs font-semibold uppercase tracking-wider text-slate-500 dark:text-slate-400", children: [_jsx("th", { className: "py-1 pr-2", children: t("credentials.colId") }), _jsx("th", { className: "py-1 pr-2", children: t("credentials.colProvider") }), _jsx("th", { className: "py-1 pr-2", children: t("credentials.colKeySecret") }), _jsx("th", { className: "py-1 pr-2", children: t("credentials.colEndpoint") }), _jsx("th", { className: "py-1" })] }) }), _jsxs("tbody", { children: [ids.map((id) => (_jsx(CredentialRow, { id: id, entry: value[id], existingIds: ids, disabled: disabled, onEntry: (e) => setEntry(id, e), onRename: (to) => renameEntry(id, to), onDelete: () => setEntry(id, undefined) }, id))), ids.length === 0 && (_jsx("tr", { children: _jsx("td", { colSpan: 5, className: "py-3 text-xs italic text-slate-400", children: t("credentials.emptyRow") }) }))] })] }) }), _jsxs("button", { type: "button", onClick: addEntry, disabled: disabled, className: "inline-flex items-center gap-1 rounded-md border border-slate-300 px-2 py-1 text-xs font-medium text-slate-700 hover:bg-slate-50 disabled:opacity-50 dark:border-slate-600 dark:text-slate-300 dark:hover:bg-slate-800", children: [_jsx(Plus, { className: "h-3.5 w-3.5" }), " ", t("credentials.addCredential")] }), _jsx("p", { className: "text-xs text-slate-400 dark:text-slate-500", children: t("credentials.help") })] }));
}
function CredentialRow({ id, entry, existingIds, disabled, onEntry, onRename, onDelete, }) {
    const t = useOrgAdminT();
    // Commit-on-blur id (stable React key while typing — the AliasMatrix lesson).
    const [draftId, setDraftId] = useState(id);
    useEffect(() => setDraftId(id), [id]);
    const collides = draftId !== id && existingIds.includes(draftId);
    // A SAVED credential can't be renamed while its secret is masked — the SPA
    // only holds the sentinel, so the server would see a masked secret under an
    // unknown id (= the stored secret silently lost; the server 400s it too).
    // Re-entering the secret unlocks the rename.
    const renameLocked = entry.secret === MASKED && draftId !== id;
    const idValid = ID_RE.test(draftId) && draftId.length <= MAX_ID_LEN && !collides && !renameLocked;
    const commitId = () => {
        if (draftId === id)
            return;
        if (idValid)
            onRename(draftId);
        else
            setDraftId(id);
    };
    const [reveal, setReveal] = useState(false);
    const isCustom = entry.kind === "custom";
    return (_jsxs("tr", { className: "border-t border-slate-100 align-top dark:border-slate-800", children: [_jsxs("td", { className: "py-2 pr-2", children: [_jsx("input", { value: draftId, onChange: (e) => setDraftId(e.target.value.toLowerCase()), onBlur: commitId, onKeyDown: (e) => {
                            if (e.key === "Enter")
                                e.currentTarget.blur();
                        }, disabled: disabled, maxLength: MAX_ID_LEN, "aria-label": t("credentials.ariaId"), title: collides
                            ? t("credentials.idCollision")
                            : renameLocked
                                ? t("credentials.renameLocked")
                                : undefined, className: `${INPUT_CLASS} w-36 font-mono text-xs ${idValid ? "" : "border-rose-400 dark:border-rose-500"}` }), entry.label !== undefined && (_jsx("div", { className: "mt-1 text-[11px] text-slate-400", children: entry.label }))] }), _jsx("td", { className: "py-2 pr-2", children: _jsx("select", { value: entry.provider, onChange: (e) => {
                        const provider = e.target.value;
                        const kind = CUSTOM_KIND_PROVIDERS.has(provider) ? "custom" : "api-key";
                        onEntry({ ...entry, provider, kind });
                    }, disabled: disabled, "aria-label": t("credentials.ariaProvider"), className: INPUT_CLASS, children: PROVIDER_OPTIONS.map((p) => (_jsx("option", { value: p, children: t(`credentials.providerLabels.${p}`) }, p))) }) }), _jsx("td", { className: "py-2 pr-2", children: _jsxs("div", { className: "flex items-center gap-1", children: [_jsx("input", { type: reveal ? "text" : "password", 
                            // Provider secret, not a login password — see the note in
                            // SearchProvidersTable.tsx. "new-password" is what browsers honour
                            // to suppress saved-password autofill on a password-typed input.
                            autoComplete: "new-password", value: entry.secret, onChange: (e) => onEntry({ ...entry, secret: e.target.value }), onFocus: (e) => {
                                // Clicking into a masked secret starts a fresh entry (the
                                // sentinel is not editable text).
                                if (entry.secret === MASKED) {
                                    onEntry({ ...entry, secret: "" });
                                    e.currentTarget.value = "";
                                }
                            }, disabled: disabled, maxLength: 8192, placeholder: t("credentials.secretPlaceholder"), "aria-label": t("credentials.ariaSecret"), className: `${INPUT_CLASS} w-44 font-mono text-xs ${entry.secret.trim() ? "" : "border-rose-400 dark:border-rose-500"}` }), _jsx("button", { type: "button", onClick: () => setReveal((r) => !r), disabled: disabled || entry.secret === MASKED, title: entry.secret === MASKED ? t("credentials.maskedHidden") : reveal ? t("common.hide") : t("common.show"), className: "text-slate-400 hover:text-slate-600 dark:hover:text-slate-300", children: reveal ? _jsx(EyeOff, { className: "h-4 w-4" }) : _jsx(Eye, { className: "h-4 w-4" }) })] }) }), _jsx("td", { className: "py-2 pr-2", children: isCustom ? (_jsxs("div", { className: "space-y-1", children: [_jsx("input", { value: entry.baseUrl ?? "", onChange: (e) => onEntry({ ...entry, baseUrl: e.target.value || undefined }), disabled: disabled, placeholder: entry.provider === "openrouter" ? t("credentials.baseUrlPlaceholderOpenrouter") : t("credentials.baseUrlPlaceholder"), "aria-label": t("credentials.ariaBaseUrl"), className: `${INPUT_CLASS} w-56 text-xs` }), _jsx("input", { value: (entry.models ?? []).join(", "), onChange: (e) => onEntry({
                                ...entry,
                                models: e.target.value
                                    .split(",")
                                    .map((s) => s.trim())
                                    .filter(Boolean),
                            }), disabled: disabled, placeholder: t("credentials.modelsPlaceholder"), "aria-label": t("credentials.ariaModels"), className: `${INPUT_CLASS} w-56 font-mono text-[11px]` })] })) : (_jsx("span", { className: "text-xs text-slate-300 dark:text-slate-600", children: "\u2014" })) }), _jsx("td", { className: "py-2 text-right", children: _jsx("button", { type: "button", onClick: onDelete, disabled: disabled, title: t("credentials.deleteCredential"), className: "text-slate-400 hover:text-rose-500", children: _jsx(Trash2, { className: "h-4 w-4" }) }) })] }));
}
//# sourceMappingURL=CredentialsTable.js.map